import React, { memo } from 'react';
import { motion } from 'framer-motion';

const css = `
  /* ── Wrapper ── */
  .semi-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
    padding: 8px;
    position: relative;
  }

  /* ── Grid of other boxes ── */
  .semi-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    width: 100%;
    max-width: 480px;
    padding: 16px 12px 20px;
    background:
      linear-gradient(180deg, transparent 0%, rgba(40,25,15,0.15) 60%, rgba(40,25,15,0.25) 100%),
      radial-gradient(ellipse 90% 50% at 50% 100%, rgba(212,175,55,0.04) 0%, transparent 70%);
    border-radius: 60% 60% 12px 12px / 20% 20% 12px 12px;
    border: 1px solid rgba(212,175,55,0.08);
    border-top: 2px solid rgba(212,175,55,0.12);
    position: relative;
  }
  /* Bancone edge line */
  .semi-grid::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(212,175,55,0.15), transparent);
  }

  /* ── Single box in grid ── */
  .semi-box {
    aspect-ratio: 3 / 3.6;
    position: relative;
    cursor: pointer;
    transition: transform 0.2s ease, filter 0.2s ease;
  }
  .semi-box:hover:not(.semi-box--disabled) {
    transform: scale(1.12) translateY(-6px);
    filter: brightness(1.2);
    z-index: 10;
  }
  .semi-box--disabled { cursor: default; }
  .semi-box--opened { opacity: 0.4; cursor: default; }

  /* Box parts */
  .sb-lid {
    position: absolute;
    top: 0; left: -1px; right: -1px; height: 28%;
    border-radius: 4px 4px 1px 1px;
    z-index: 2;
    transition: transform 0.5s ease, opacity 0.5s ease;
  }
  .sb-lid--closed {
    background: linear-gradient(180deg, #42A5F5, #2196F3 50%, #1976D2);
    border: 1px solid rgba(66,165,245,0.4);
    box-shadow: 0 2px 6px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15);
  }
  .sb-lid--open {
    background: linear-gradient(180deg, #0d1b2a, #0a1520);
    border: 1px solid rgba(30,50,80,0.2);
    transform: rotateX(-110deg);
    transform-origin: top center;
    opacity: 0.3;
  }

  .sb-body {
    position: absolute;
    bottom: 0; left: 0; right: 0; height: 78%;
    border-radius: 3px 3px 5px 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    overflow: hidden;
  }
  .sb-body--closed {
    background: linear-gradient(180deg, #2196F3 0%, #1976D2 30%, #1565C0 60%, #0D47A1 100%);
    border: 1px solid rgba(33,150,243,0.4);
    box-shadow: 0 4px 14px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1);
  }
  .sb-body--open {
    background: linear-gradient(180deg, #0d1b2a, #060e15);
    border: 1px solid rgba(30,50,80,0.2);
    box-shadow: inset 0 3px 10px rgba(0,0,0,0.6);
  }

  .sb-number {
    background: linear-gradient(135deg, #C8962A, #F0D060 40%, #D4AF37 70%, #C8962A);
    color: #2a1800;
    font-family: var(--font-display);
    font-size: clamp(0.6rem, 1.5vw, 0.85rem);
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 2px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3);
    z-index: 3;
    text-align: center;
    min-width: 18px;
  }
  .sb-region {
    font-size: clamp(0.3rem, 0.8vw, 0.42rem);
    font-family: var(--font-display);
    font-weight: 600;
    color: rgba(255,255,255,0.8);
    text-transform: uppercase;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    padding: 0 2px;
    z-index: 3;
  }
  .sb-prize-tag {
    font-family: var(--font-mono);
    font-size: clamp(0.3rem, 0.7vw, 0.4rem);
    font-weight: 700;
    color: #F0D060;
    z-index: 3;
  }
  .sb-prize-reveal {
    position: absolute;
    bottom: 10%;
    left: 1px; right: 1px;
    font-family: var(--font-mono);
    font-size: clamp(0.4rem, 1vw, 0.55rem);
    font-weight: 700;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    z-index: 5;
  }
  .sb-prize-reveal--high { color: #F0D060; text-shadow: 0 0 8px rgba(212,175,55,0.6); }
  .sb-prize-reveal--low { color: #5dade2; text-shadow: 0 0 8px rgba(52,152,219,0.6); }

  /* Shine sweep */
  .sb-shine {
    position: absolute;
    top: 0; left: -100%; width: 50%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
    animation: semiShine 5s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes semiShine {
    0% { left: -100%; }
    50%, 100% { left: 200%; }
  }

  /* Side depth */
  .sb-side {
    position: absolute;
    bottom: 0; right: -2px; width: 2px; height: 78%;
    background: linear-gradient(180deg, #0D47A1, #062a64);
    border-radius: 0 2px 2px 0;
    opacity: 0.6;
  }

  /* ── Player box area ── */
  .semi-player-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
  }
  .semi-player-label {
    font-family: var(--font-display);
    font-size: 0.7rem;
    font-weight: 600;
    color: #D4AF37;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    text-shadow: 0 0 15px rgba(212,175,55,0.4);
  }
  .semi-player-box {
    width: 68px;
    height: 82px;
    position: relative;
  }
  @keyframes playerFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }
  @keyframes playerGlowRing {
    0%, 100% { box-shadow: 0 0 15px rgba(212,175,55,0.3), 0 0 30px rgba(212,175,55,0.1); }
    50% { box-shadow: 0 0 25px rgba(212,175,55,0.6), 0 0 50px rgba(212,175,55,0.2); }
  }
  .semi-player-box .sb-glow {
    position: absolute;
    inset: -6px;
    border-radius: 10px;
    border: 2px solid rgba(212,175,55,0.25);
    animation: playerGlowRing 2.5s ease-in-out infinite;
    pointer-events: none;
  }
  /* Gold lid for player */
  .sb-lid--gold {
    background: linear-gradient(180deg, #F0D060, #D4AF37 50%, #B8960C) !important;
    border: 2px solid rgba(240,208,96,0.5) !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.25) !important;
  }
  /* Gold body for player */
  .sb-body--gold {
    background: linear-gradient(180deg, #D4AF37, #B8960C 30%, #8B6914 70%, #6B4F0E) !important;
    border: 2px solid rgba(240,208,96,0.5) !important;
    box-shadow: 0 6px 20px rgba(212,175,55,0.3), 0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2) !important;
  }
  .sb-number--player {
    background: linear-gradient(135deg, #1565C0, #2196F3 50%, #1565C0) !important;
    color: #fff !important;
    text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  }
  .sb-side--gold {
    background: linear-gradient(180deg, #8B6914, #5a3d0a) !important;
  }

  /* ── Responsive ── */
  @media (max-width: 900px) {
    .semi-wrap { gap: 4px; padding: 2px; }
    .semi-grid {
      grid-template-columns: repeat(5, 1fr);
      gap: 4px;
      padding: 8px 6px 10px;
      max-width: 310px;
      border-radius: 40% 40% 8px 8px / 12% 12% 8px 8px;
    }
    .semi-player-area { gap: 2px; margin-top: 2px; }
    .semi-player-box { width: 48px; height: 58px; }
    .semi-player-label { font-size: 0.5rem; letter-spacing: 0.1em; }
  }
  @media (max-width: 480px) {
    .semi-grid {
      grid-template-columns: repeat(5, 1fr);
      gap: 3px;
      max-width: 275px;
      padding: 6px 4px 8px;
    }
    .semi-player-box { width: 42px; height: 50px; }
    .semi-player-label { font-size: 0.45rem; }
    .sb-number { font-size: clamp(0.5rem, 1.2vw, 0.7rem) !important; padding: 1px 4px !important; }
    .sb-region { display: none !important; }
  }
  @media (max-width: 380px) {
    .semi-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 3px;
      max-width: 240px;
    }
    .semi-player-box { width: 38px; height: 46px; }
  }
`;

function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  return value.toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0, maximumFractionDigits: 2,
  });
}

function prizeDisplay(box) {
  if (box.prize_label && (box.prize_value === 0 || box.prize_value === undefined)) return box.prize_label;
  if (box.prize_value !== undefined) return formatEuro(box.prize_value);
  return null;
}

export default function SemicircleBoxes({ boxes = [], playerBoxNumber, onBoxClick, disabled = false, showAllPrizes = false }) {
  const sorted = [...boxes].sort((a, b) => a.box_number - b.box_number);
  const otherBoxes = sorted.filter(b => b.box_number !== playerBoxNumber);
  const playerBox = sorted.find(b => b.box_number === playerBoxNumber);

  return (
    <div className="semi-wrap">
      <style>{css}</style>

      {/* Grid of other boxes on bancone */}
      <div className="semi-grid">
        {otherBoxes.map((box, i) => (
          <SmallBox
            key={box.box_number}
            box={box}
            index={i}
            clickable={!box.is_opened && !disabled}
            onBoxClick={onBoxClick}
            showPrize={showAllPrizes}
          />
        ))}
      </div>

      {/* Player's box at bottom */}
      {playerBox && (
        <div className="semi-player-area">
          <div className="semi-player-label">IL TUO PACCO</div>
          <motion.div
            className="semi-player-box"
            style={{ animation: 'playerFloat 3s ease-in-out infinite' }}
            initial={{ scale: 0, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
          >
            <div className="sb-glow" />
            <div className="sb-lid sb-lid--closed sb-lid--gold">
              {/* Ridge */}
              <div style={{
                position: 'absolute', top: '50%', left: '15%', right: '15%', height: '2px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transform: 'translateY(-50%)',
              }} />
            </div>
            <div className="sb-body sb-body--closed sb-body--gold">
              <div className="sb-number sb-number--player" style={{ fontSize: '0.95rem', padding: '2px 10px' }}>
                {playerBox.box_number}
              </div>
              {playerBox.region_name && (
                <div className="sb-region" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.45rem' }}>
                  {playerBox.region_name}
                </div>
              )}
              {showAllPrizes && playerBox.prize_value !== undefined && (
                <div className="sb-prize-tag" style={{ color: '#1565C0', fontSize: '0.4rem' }}>
                  {prizeDisplay(playerBox)}
                </div>
              )}
              <div className="sb-shine" />
            </div>
            <div className="sb-side sb-side--gold" style={{ width: '3px', right: '-3px' }} />
          </motion.div>
        </div>
      )}
    </div>
  );
}

const SmallBox = memo(function SmallBox({ box, index, clickable, onBoxClick, showPrize }) {
  const opened = box.is_opened;
  const isHigh = box.prize_value !== undefined && box.prize_value >= 10000;
  const cls = `semi-box ${opened ? 'semi-box--opened' : ''} ${!clickable ? 'semi-box--disabled' : ''}`;

  return (
    <div
      className={cls}
      onClick={() => clickable && onBoxClick?.(box.box_number)}
    >
      {/* Lid */}
      <div className={`sb-lid ${opened ? 'sb-lid--open' : 'sb-lid--closed'}`} />

      {/* Body */}
      <div className={`sb-body ${opened ? 'sb-body--open' : 'sb-body--closed'}`}>
        {!opened && (
          <>
            <div className="sb-number">{box.box_number}</div>
            {box.region_name && <div className="sb-region">{box.region_name}</div>}
            {showPrize && box.prize_value !== undefined && (
              <div className="sb-prize-tag">{prizeDisplay(box)}</div>
            )}
            <div className="sb-shine" style={{ animationDelay: `${index * 0.2}s` }} />
          </>
        )}
      </div>

      {/* Prize reveal */}
      {opened && (box.prize_value !== undefined || box.prize_label) && (
        <div className={`sb-prize-reveal ${isHigh ? 'sb-prize-reveal--high' : 'sb-prize-reveal--low'}`}>
          {prizeDisplay(box)}
        </div>
      )}

      {/* Side depth */}
      {!opened && <div className="sb-side" />}
    </div>
  );
});
